import { watch } from 'vue'
import { useRouter } from 'vue-router'
import get from 'lodash/get'
import maxBy from 'lodash/maxBy'
import { useCurrentUserStore } from '~/stores/user-store.store.ts'
import { useJackpotStore } from '~/stores/jackpot.store.ts'
import { usePromotionUserStore } from '~/stores/promotion-user.store.ts'
import { useResetStore } from '~/composables/common/use-reset-store'
import { useResetCookie } from '~/composables/common/use-reset-cookie'
import type {
  JackpotBanner,
  IJackpotSocketResponse,
  IDataMessageSocketResponse,
  IPromotionUserSocketResponse
} from '~/composables/common/interface'
import { notificationPopup } from '~utils/modal/notify.ts'
import { modalList } from '~resources/popup-notification.ts'
import { SOCKET_MESSAGE, REST_FORBIDDEN, STATUS_CODE } from '~/constants'

export function useSocket() {
  const { $io: socket } = useNuxtApp()
  const storeCurrentUser = useCurrentUserStore()
  const jackpotStoreState = useJackpotStore()
  const storePromotion = usePromotionUserStore()
  const resetStore = useResetStore()
  const { resetCookie } = useResetCookie()
  const router = useRouter()
  const currentUserId = ref('')
  const currentToken = ref('')
  handleSubscribeData()

  watch(
    () => storeCurrentUser.data.token,
    data => {
      socket.emit('authenticate', { token: data })
      if (data) {
        currentUserId.value = storeCurrentUser.data?.id
        currentToken.value = data
        setTimeout(() => {
          handleAuthorized()
        }, 1000)
      } else {
        handleUnauthorized()
      }
    },
    {
      immediate: true
    }
  )
  function subscribeWallet(): void {
    if (socket) {
      socket.emit('subscribeWallet', { userId: storeCurrentUser.data.id, token: storeCurrentUser.data.token })
    }
  }

  function unSubscribeWallet(): void {
    if (socket) {
      socket.emit('unSubscribeWallet', { userId: currentUserId.value, token: currentToken.value })
    }
  }

  function subscribeUserPlan(): void {
    if (socket) {
      socket.emit('subscribeUserPlan', { userId: storeCurrentUser.data.id, token: storeCurrentUser.data.token })
    }
  }
  function unSubscribeUserPlan(): void {
    if (socket) {
      socket.emit('unSubscribeUserPlan', { userId: currentUserId.value, token: currentToken.value })
    }
  }

  function subscribeJackpot(): void {
    if (socket) {
      socket.emit('subscribeJackpot')
    }
  }

  function subscribeJackpotBanner(): void {
    if (socket) {
      socket.emit('subscribeGameJackpot')
    }
  }

  async function openModalExpiredWarning(): Promise<void> {
    resetCookie()
    await router.push('/')
    notificationPopup(modalList.LOGIN_EXPIRED)
  }

  function subscribeInformation(): void {
    if (socket) {
      socket.emit('subscribeInformation')
    }
  }

  function handleUnauthorized(): void {
    unSubscribeWallet()
    unSubscribeUserPlan()
  }

  function handleAuthorized(): void {
    subscribeWallet()
    subscribeUserPlan()
  }

  function handleSubscribeData(): void {
    setTimeout(() => {
      subscribeJackpot()
      subscribeInformation()
      subscribeJackpotBanner()
    }, 1000)
  }

  function updateCurrentUserWallet(message: IDataMessageSocketResponse): void {
    const newBalance = get(message, 'gpWalletData[0].balance', 0) as number

    if (storeCurrentUser.data.token) {
      const userStore = localStorage.getItem('userData')

      if (!userStore || !JSON.parse(userStore)?.data?.token) {
        setTimeout(() => {
          handleUnauthorized()
          resetStore.all()
          void openModalExpiredWarning()
        })
      } else {
        storeCurrentUser.setCurrentUser({
          ...storeCurrentUser.data,
          ...message.currentUser,
          wallet: {
            ...message.currentUser.wallet,
            balance: newBalance
          }
        })
      }
    }
  }

  socket.on(SOCKET_MESSAGE.MESSAGE, async function message(message: IDataMessageSocketResponse) {
    const isUnauthorized =
      (!message?.data ||
        Number(get(message, 'data.code', 0)) === STATUS_CODE.CODE_UNAUTHORIZED ||
        get(message, 'data.code', '').toString() === REST_FORBIDDEN ||
        message.error) ??
      !message.currentUser?.token

    if (isUnauthorized) {
      handleUnauthorized()
      if (storeCurrentUser.data.username) {
        setTimeout(() => {
          resetStore.all()
          void openModalExpiredWarning()
        })
      }
      return
    }

    if (message.code === STATUS_CODE.CODE_SUCCESS && message.currentUser && message.currentUser.token) {
      updateCurrentUserWallet(message)
    }
  })

  socket.on(SOCKET_MESSAGE.JACKPOT, function jackpot(message: IJackpotSocketResponse = { data: null }) {
    if (message.data) {
      jackpotStoreState.setJackpot(message.data)
    }
  })

  socket.on(SOCKET_MESSAGE.JACKPOTBANNER, function jackpot(message: JackpotBanner[] = []) {
    if (message.length) {
      const gameItem = maxBy(message.filter(item => item.on), 'currentValue')
      if (gameItem) {
        jackpotStoreState.setXocDiaJackpot({
          gameId: gameItem.id,
          gameName: gameItem.name,
          value: gameItem.currentValue,
        })
        return
      }
      jackpotStoreState.setXocDiaJackpot({
        gameId: '',
        gameName: '',
        value: 0
      })
    }
  })

  socket.on(SOCKET_MESSAGE.MAINTENANCE, function maintenance(message = { data: null }) {
    if (message.data) {
      // todo
    }
  })

  socket.on(SOCKET_MESSAGE.PLAN, function plan(message: IPromotionUserSocketResponse = { data: null }) {
    if (message.data && Object.entries(message.data).length > 0) {
      const promotionStore = storePromotion.promotionUser
      const promotionData = message.data
      storePromotion.setPromotionUser({
        ...promotionStore,
        ...promotionData
      })
    }
  })

  socket.on(SOCKET_MESSAGE.DISCONNECT, () => {
    setTimeout(() => {
      handleSubscribeData()
      if (storeCurrentUser.data.token) {
        handleAuthorized()
      }
    }, 1000)
  })

  socket.on(SOCKET_MESSAGE.CONNECT, () => {
    if (storeCurrentUser.data.token) {
      socket.emit('authenticate', { token: storeCurrentUser.data.token })
    }
  })

  return {}
}
